/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';
import moment from 'moment';

import React, { Component } from 'react';
import { FlexLayout, FlexItem } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';
import { getTid } from 'utils/tid';
import { FILTER_TYPE, FILTER_RANGE_TYPE, FILTER_RANGE_SUBTYPE } from './filterProps';

import FilterTypeSelector from './FilterTypeSelector';
import DateTimeFilter from './DateTimeFilter';
import SelectValueFilterPane from './SelectValueFilterPane';
import FilterPreferences from './FilterPreferences';
import FilterLoadingSpinner from './FilterLoadingSpinner';

import './DateTimeFilterPane.scss';

export default class DateTimeFilterPane extends Component<any, any> {
	static propTypes = {
		// props for DateTimeFilter
		filterSelectionType: PropTypes.oneOf([FILTER_TYPE.RANGE, FILTER_TYPE.SELECT_VALUE]),
		fromValue: PropTypes.string,
		toValue: PropTypes.string,
		rangeType: PropTypes.string,
		rangeSubtype: PropTypes.string,
		dataType: PropTypes.string,
		includeNulls: PropTypes.bool,
		inverted: PropTypes.bool,
		onChange: PropTypes.func.isRequired,
		onClear: PropTypes.func,
		locale: PropTypes.string.isRequired,
		timezone: PropTypes.string,
		// Props for SelectValueFilter
		totalValues: PropTypes.number.isRequired,
		selectedValuesCount: PropTypes.number,
		loading: PropTypes.bool,
		onClick: PropTypes.func.isRequired,
		onSearch: PropTypes.func,
		allowSearch: PropTypes.bool,
		allowIncludeNulls: PropTypes.bool,
		contentRenderer: PropTypes.func,
		getValue: PropTypes.func,
		showLabels: PropTypes.bool,
		allowSwitch: PropTypes.bool,
		showFilterPreferences: PropTypes.bool,
		filterType: PropTypes.oneOf(['compact', 'full']),
		readOnly: PropTypes.bool,
		hasMoreData: PropTypes.bool,
		invertDisabled: PropTypes.bool,
		setValidationStatus: PropTypes.func,
		isDirty: PropTypes.bool,
		isValid: PropTypes.bool,
		validRangeTypeOptions: PropTypes.array,
	};

	static defaultProps = {
		filterSelectionType: FILTER_TYPE.RANGE,
		fromValue: 0,
		toValue: 0,
		rangeType: FILTER_RANGE_TYPE.BETWEEN,
		rangeSubtype: FILTER_RANGE_SUBTYPE.DATE_AND_TIME,
		dataType: 'datetime',
		includeNulls: false,
		selectedValuesCount: 0,
		loading: false,
		showLabels: true,
		timezone: 'UTC',
		showFilterPreferences: true,
		filterType: 'compact',
		readOnly: false,
		allowSwitch: true,
		allowSearch: true,
		allowIncludeNulls: true,
		hasMoreData: false,
		invertDisabled: false,
		isDirty: false,
		isValid: true,
	};

	_onChange = result => {
		// Append the filter type to the result
		const res = {
			...result,
			includeNulls: this.props.includeNulls,
			filterSelectionType: this.props.filterSelectionType,
		};

		this.validate(result);

		this.props.onChange(res);
	};

	validate = result => {
		const { rangeType, toValue, fromValue, format } = result;
		const { setValidationStatus } = this.props;
		const from = moment.utc(fromValue, format);
		const to = moment.utc(toValue, format);

		if (rangeType === 'between' || rangeType === 'not between') {
			setValidationStatus(!to.isBefore(moment.utc(from)));
		} else {
			setValidationStatus(true);
		}
	};

	_onFilterTypeChanged = filterSelectionType => {
		const result = {
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			includeNulls: this.props.includeNulls,
			inverted: this.props.inverted,
			filterSelectionType,
		};

		this.props.onChange(result);
	};

	_buildResult = () => {
		const result = {
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			includeNulls: this.props.includeNulls,
			inverted: this.props.inverted,
			rangeType: this.props.rangeType,
			rangeSubtype: this.props.rangeSubtype,
		};

		return result;
	};

	_onPreferencesChanged = preferences => {
		const result = {
			...this._buildResult(),
			...preferences,
			filterSelectionType: this.props.filterSelectionType,
		};

		this.props.onChange(result);
	};

	_renderDateTimeFilterPane = () => {
		const {
			fromValue,
			toValue,
			rangeType,
			rangeSubtype,
			dataType,
			includeNulls,
			showLabels,
			inverted,
			selectedValuesCount,
			locale,
			timezone,
			totalValues,
			loading,
			onClick,
			onSearch,
			getValue,
			contentRenderer,
			allowSearch,
			filterSelectionType,
			allowSwitch,
			showFilterPreferences,
			filterType,
			readOnly,
			allowIncludeNulls,
			hasMoreData,
			invertDisabled,
			setValidationStatus,
			validRangeTypeOptions,
		} = this.props;

		let spacing = 2;

		if (filterType === 'full') {
			spacing = 3;
		}

		const borderStyle = {
			'border-color': '#F2F2F2',
			'border-width': '1px',
			'border-style': 'solid',
		};

		return (
			<FlexLayout fullHeight direction="column" data-tid={getTid('dateTimeFilterPane')}>
				{allowSwitch && (
					<FlexItem gutter={[0, 0, spacing, 0]}>
						<FilterTypeSelector
							type={filterSelectionType}
							onChange={this._onFilterTypeChanged}
							readOnly={readOnly}
						/>
					</FlexItem>
				)}
				{filterSelectionType === FILTER_TYPE.RANGE ? (
					<FlexItem grow>
						<DateTimeFilter
							fromValue={fromValue}
							toValue={toValue}
							rangeType={rangeType}
							rangeSubtype={rangeSubtype}
							dataType={dataType}
							inverted={inverted}
							locale={locale}
							timezone={timezone}
							onChange={this._onChange}
							showLabels={showLabels}
							setValidationStatus={setValidationStatus}
							readOnly={readOnly}
							isDirty={this.props.isDirty}
							isValid={this.props.isValid}
							validRangeTypeOptions={validRangeTypeOptions}
						/>
					</FlexItem>
				) : (
					<FlexItem grow margin={[0, 0, 2, 0]} style={borderStyle}>
						<SelectValueFilterPane
							totalValues={totalValues}
							selectedValuesCount={selectedValuesCount}
							loading={loading}
							allowSearch={allowSearch}
							onClick={onClick}
							onChange={this._onChange}
							onSearch={onSearch}
							getValue={getValue}
							contentRenderer={contentRenderer}
							readOnly={readOnly}
							hasMoreData={hasMoreData}
						/>
					</FlexItem>
				)}
				{showFilterPreferences && (
					<FilterPreferences
						inverted={inverted}
						includeNulls={includeNulls}
						onChange={this._onPreferencesChanged}
						onClear={this.props.onClear}
						showIncludeNull={
							allowIncludeNulls && filterSelectionType === FILTER_TYPE.RANGE
						}
						showInvertSelected={filterSelectionType !== FILTER_TYPE.RANGE}
						selectedValuesCount={selectedValuesCount}
						returnToTree={() => {}}
						blankValuesLabel={stringResource.get('includeBlankDate')}
						readOnly={readOnly}
						invertDisabled={invertDisabled}
					/>
				)}
			</FlexLayout>
		);
	};

	render() {
		const { loading } = this.props;

		if (loading) {
			return <FilterLoadingSpinner />;
		}

		return this._renderDateTimeFilterPane();
	}
}
